'use client';

import type { z } from 'zod';

import type { FormSchema } from '~/landing/core/simulator/future-savings-planner';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  FieldWrapper,
  Form,
  FormField,
  PhoneInput,
  SimpleCard,
  TextInput,
  ToggleGroup,
  toast,
} from '@polyseme/design-system';
import { civilities } from '@polyseme/models/penelop/common/request-client';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';

import { INFORMATION_LINK } from '~/landing/core/links';
import { useMarketingData } from '~/landing/domains/crm/create-lead/hooks';
import { createLeadSchema } from '~/landing/domains/crm/create-lead/schema';
import { generateSimulationDescription } from '~/landing/domains/crm/create-lead/utils';
import { api } from '~/landing/trpc/client';

const useCivilityOptions = () => {
  const t = useTranslations('landing.common.civility');

  return civilities.map((civility) => ({
    label: t(civility),
    value: civility,
  }));
};

const createLeadFormSchema = createLeadSchema.omit({
  googleClickId: true,
  marketingCampaign: true,
  sourcePartner: true,
});
type CreateLeadForm = z.infer<typeof createLeadFormSchema>;

export const ContactTab = ({ simulation }: { simulation: FormSchema }) => {
  const t = useTranslations('landing.retraite.contactCard.tabs.contactMe');
  const tCommon = useTranslations('landing.retraite');
  const router = useRouter();

  const form = useForm<CreateLeadForm>({
    resolver: zodResolver(createLeadFormSchema),
  });

  const { marketingCampaign, sourcePartner, googleClickId } =
    useMarketingData();

  const { isPending, mutateAsync: createLead } =
    api.crm.createLead.useMutation();

  const civilityOptions = useCivilityOptions();

  const onSubmit = async (lead: CreateLeadForm) => {
    try {
      await createLead({
        ...lead,
        googleClickId,
        marketingCampaign,
        sourcePartner,
        description: generateSimulationDescription({
          ...simulation,
          riskProfileLabel: tCommon(
            `riskProfile.${simulation.futureSavingsPlanner.riskProfile}.name`
          ),
        }),
      });
      router.push('/confirmation');
    } catch {
      toast({
        title: t('error.title'),
        description: t('error.description'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <SimpleCard className="w-full">
          <Form {...form}>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <FormField
                control={form.control}
                name="salutation"
                render={({ field }) => (
                  <FieldWrapper
                    className="col-span-full"
                    label={t(`form.${field.name}`)}
                  >
                    <ToggleGroup
                      name={field.name}
                      options={civilityOptions}
                      type="single"
                      value={field.value}
                      onValueChange={field.onChange}
                    />
                  </FieldWrapper>
                )}
              />

              <FormField
                control={form.control}
                name="firstname"
                render={({ field }) => (
                  <FieldWrapper label={t(`form.${field.name}`)}>
                    <TextInput autoComplete="given-name" {...field} />
                  </FieldWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="lastname"
                render={({ field }) => (
                  <FieldWrapper label={t(`form.${field.name}`)}>
                    <TextInput autoComplete="family-name" {...field} />
                  </FieldWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FieldWrapper label={t(`form.${field.name}`)}>
                    <TextInput {...field} autoComplete="email" type="email" />
                  </FieldWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FieldWrapper label={t(`form.${field.name}`)}>
                    <PhoneInput {...field} />
                  </FieldWrapper>
                )}
              />
              <FormField
                control={form.control}
                name="postalCode"
                render={({ field }) => (
                  <FieldWrapper label={t(`form.${field.name}`)}>
                    <TextInput {...field} autoComplete="postal-code" />
                  </FieldWrapper>
                )}
              />
            </div>
          </Form>
        </SimpleCard>
        <p className="text-xs text-muted-foreground">
          {t.rich('information', {
            link: (link) => (
              <a
                className="font-semibold underline"
                href={INFORMATION_LINK}
                rel="noreferrer"
                target="_blank"
              >
                {link}
              </a>
            ),
          })}
        </p>
        <div className="flex justify-center">
          <Button
            className="w-full md:mx-auto md:w-[unset]"
            isLoading={isPending}
            type="submit"
          >
            {t('submit')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
